import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonAvatar,
  IonChip,
  IonLabel,
  IonAlert,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons
} from '@ionic/react';
import {
  personOutline,
  logOutOutline,
  settingsOutline,
  carOutline,
  starOutline,
  arrowBackOutline,
  mailOutline,
  closeOutline
} from 'ionicons/icons';

import './ProfileScreen.css';

const Profile = ({ user }) => {
  const navigate = useNavigate();
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const handleLogout = () => {
    // Remove user data from Web Storage
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');

    // Clear all cookies for current domain
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
    });

    // Refresh the page; auth guard will redirect to login/root automatically
    window.location.reload();
  };

  const goBack = () => {
    navigate(-1);
  };

  // JotForm iframe source
  const jotformSrc = "https://form.jotform.com/252999047776074";

  return (
    <IonPage className="profile-page">
      <IonContent fullscreen className="profile-content">
        <div className="content-container">

          {/* Header with Back Button */}
          <div className="user-header">
            <IonButton fill="clear" onClick={goBack} className="back-button">
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </div>

          {/* Profile Card */}
          <div className="profile-card">
            <IonAvatar className="profile-avatar-large">
              {user?.picture ? (
                <img src={user.picture} alt="Profile" />
              ) : (
                <div className="avatar-placeholder-lg">
                  {user?.given_name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
            </IonAvatar>

            <div className="user-details">
              <h2>{user?.name || user?.given_name || 'User'}</h2>
              <p>{user?.email || 'No email available'}</p>
              <IonChip color="primary">
                <IonIcon icon={starOutline} />
                <IonLabel>Verified User</IonLabel>
              </IonChip>
            </div>
          </div>

          {/* Action Grid */}
          <div className="profile-actions">
            <h4 className="section-title">Account</h4>
            <div className="action-grid">
              <div className="action-card disabled">
                <div className="action-icon">
                  <IonIcon icon={personOutline} />
                </div>
                <div className="action-content">
                  <h5>Edit Profile</h5>
                  <p>Update your personal information</p>
                  <span className="coming-soon-badge">Coming Soon</span>
                </div>
              </div>

              <div className="action-card disabled">
                <div className="action-icon">
                  <IonIcon icon={carOutline} />
                </div>
                <div className="action-content">
                  <h5>Vehicle</h5>
                  <p>Manage your car details</p>
                  <span className="coming-soon-badge">Coming Soon</span>
                </div>
              </div>

              <div className="action-card disabled">
                <div className="action-icon">
                  <IonIcon icon={settingsOutline} />
                </div>
                <div className="action-content">
                  <h5>Settings</h5>
                  <p>App preferences</p>
                  <span className="coming-soon-badge">Coming Soon</span>
                </div>
              </div>

              <div className="action-card" onClick={() => setShowContactModal(true)}>
                <div className="action-icon">
                  <IonIcon icon={mailOutline} />
                </div>
                <div className="action-content">
                  <h5>Contact Us</h5>
                  <p>Get in touch with our team</p>
                </div>
              </div>

              <div className="action-card logout-card" onClick={() => setShowLogoutAlert(true)}>
                <div className="action-icon logout-icon">
                  <IonIcon icon={logOutOutline} />
                </div>
                <div className="action-content">
                  <h5>Sign Out</h5>
                  <p>Log out of your account</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Logout Confirmation */}
        <IonAlert
          isOpen={showLogoutAlert}
          onDidDismiss={() => setShowLogoutAlert(false)}
          header="Sign Out"
          message="Are you sure you want to sign out?"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel'
            },
            {
              text: 'Sign Out',
              role: 'confirm',
              handler: handleLogout
            }
          ]}
        />

        {/* Contact Us Modal */}
        <IonModal
          isOpen={showContactModal}
          onDidDismiss={() => setShowContactModal(false)}
          className="contact-modal"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Contact Us</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowContactModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <iframe
              src={jotformSrc}
              title="Contact Us Form"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px'
              }}
              allowFullScreen
            />
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Profile;